import React, { useMemo, useState } from "react";
import { Box, Typography, IconButton, useMediaQuery } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { localeFor } from "../../../utils/region";
import {
  accent, navy, textPrimary, textMuted, textSecondary, font, border, surface, surfaceRaised,
} from "../../../layouts/schoolPortal/schoolPortalTheme";

/**
 * A month at a glance, beside the week grid rather than replacing it.
 *
 * WHY BOTH VIEWS EXIST
 * The week grid is for editing — dragging out availability, seeing a Tuesday
 * gap. It is the wrong tool for "am I free the week of the 20th" or "how busy
 * is next month", which is what an instructor is actually asked on the phone.
 * A month view answers that in one glance and cannot edit anything, which is
 * why it can afford to be dense.
 *
 * DENSITY, NOT DETAIL. Each day shows how much is booked, not what. Trying to
 * render session titles in a month cell produces six truncated words nobody can
 * read; a count and a bar are legible at a glance and honest about being a
 * summary.
 */
const DAY_MS = 24 * 60 * 60 * 1000;
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const startOfGrid = (d) => {
  const first = startOfMonth(d);
  const day = first.getDay();                 // 0 = Sunday
  const back = day === 0 ? 6 : day - 1;       // grid starts Monday
  return new Date(first.getTime() - back * DAY_MS);
};
const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

export default function MonthAvailabilityCalendar({
  bookings = [],
  blocks = [],
  region,
  onSelectDay,
}) {
  const [cursor, setCursor] = useState(() => new Date());
  const isNarrow = useMediaQuery("(max-width:720px)");
  const locale = localeFor(region);
  const today = new Date();

  const days = useMemo(() => {
    const start = startOfGrid(cursor);
    // Always six rows. A grid that changes height between months makes the page
    // jump when you page through it, which is the one thing a calendar must not
    // do while someone is scanning it.
    return Array.from({ length: 42 }, (_, i) => new Date(start.getTime() + i * DAY_MS));
  }, [cursor]);

  const byDay = useMemo(() => {
    const map = new Map();
    for (const b of bookings) {
      const d = new Date(b.start_time);
      const key = d.toDateString();
      const entry = map.get(key) || { count: 0, minutes: 0 };
      entry.count += 1;
      entry.minutes += Math.max(0, Math.round((new Date(b.end_time) - d) / 60000));
      map.set(key, entry);
    }
    return map;
  }, [bookings]);

  const blockedDays = useMemo(() => {
    const set = new Set();
    for (const bl of blocks) {
      let d = new Date(bl.from || bl.start_time);
      const end = new Date(bl.to || bl.end_time);
      while (d <= end) {
        set.add(d.toDateString());
        d = new Date(d.getTime() + DAY_MS);
      }
    }
    return set;
  }, [blocks]);

  const busiest = Math.max(1, ...[...byDay.values()].map((v) => v.minutes));
  const move = (n) => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + n, 1));

  return (
    <Box sx={{ background: surface, border: `1px solid ${border}`, borderRadius: "12px", p: { xs: 1.5, sm: 2 } }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
        <IconButton size="small" onClick={() => move(-1)} aria-label="Previous month"><ChevronLeftIcon /></IconButton>
        <Typography sx={{ fontFamily: font, fontWeight: 600, color: textPrimary, fontSize: { xs: 14, sm: 16 } }}>
          {cursor.toLocaleDateString(locale, { month: "long", year: "numeric" })}
        </Typography>
        <IconButton size="small" onClick={() => move(1)} aria-label="Next month"><ChevronRightIcon /></IconButton>
      </Box>

      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: { xs: 0.25, sm: 0.5 } }}>
        {WEEKDAYS.map((d) => (
          <Typography key={d} sx={{
            fontFamily: font, fontSize: { xs: 10, sm: 11 }, color: textMuted,
            textAlign: "center", fontWeight: 600, letterSpacing: 0.4, pb: 0.5,
          }}>
            {isNarrow ? d[0] : d}
          </Typography>
        ))}

        {days.map((d) => {
          const inMonth = d.getMonth() === cursor.getMonth();
          const entry = byDay.get(d.toDateString());
          const blocked = blockedDays.has(d.toDateString());
          const isToday = sameDay(d, today);
          const fill = entry ? Math.max(0.12, entry.minutes / busiest) : 0;

          return (
            <Box
              key={d.toISOString()}
              onClick={() => onSelectDay?.(d)}
              role={onSelectDay ? "button" : undefined}
              tabIndex={onSelectDay ? 0 : undefined}
              onKeyDown={(e) => { if (onSelectDay && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); onSelectDay(d); } }}
              sx={{
                position: "relative",
                minHeight: { xs: 44, sm: 62 },
                borderRadius: "8px",
                border: `1px solid ${isToday ? accent : border}`,
                background: blocked ? "repeating-linear-gradient(45deg, #f2ede4, #f2ede4 4px, #ffffff 4px, #ffffff 8px)" : surfaceRaised,
                // Out-of-month days stay visible but recede: removing them
                // breaks the grid, and showing them at full strength makes the
                // month boundary impossible to see.
                opacity: inMonth ? 1 : 0.38,
                cursor: onSelectDay ? "pointer" : "default",
                p: { xs: 0.5, sm: 0.75 },
                overflow: "hidden",
                "&:hover": onSelectDay ? { borderColor: accent } : {},
                "&:focus-visible": { outline: `2px solid ${accent}`, outlineOffset: 2 },
              }}
            >
              <Typography sx={{
                fontFamily: font, fontSize: { xs: 11, sm: 12 },
                fontWeight: isToday ? 700 : 500,
                color: isToday ? accent : textSecondary,
              }}>
                {d.getDate()}
              </Typography>

              {entry && (
                <>
                  <Typography sx={{ fontFamily: font, fontSize: { xs: 10, sm: 11 }, color: textPrimary, fontWeight: 600, lineHeight: 1.2 }}>
                    {entry.count}
                    <Box component="span" sx={{ display: { xs: "none", sm: "inline" }, fontWeight: 400, color: textMuted }}>
                      {entry.count === 1 ? " lesson" : " lessons"}
                    </Box>
                  </Typography>
                  {/* Relative load, so a heavy week is visible without reading
                      a single number. Scaled against the busiest day in view. */}
                  <Box sx={{
                    position: "absolute", left: 0, bottom: 0, height: 3,
                    width: `${Math.round(fill * 100)}%`, background: navy, opacity: 0.75,
                  }} />
                </>
              )}

              {blocked && !entry && (
                <Typography sx={{ fontFamily: font, fontSize: { xs: 9, sm: 10 }, color: textMuted, mt: 0.25 }}>
                  Away
                </Typography>
              )}
            </Box>
          );
        })}
      </Box>

      <Box sx={{ display: "flex", gap: 2, mt: 1.5, flexWrap: "wrap" }}>
        <Legend swatch={<Box sx={{ width: 14, height: 3, background: navy, opacity: 0.75 }} />} label="Booked load" />
        <Legend swatch={<Box sx={{ width: 12, height: 12, borderRadius: "3px", border: `1px solid ${border}`, background: "repeating-linear-gradient(45deg,#f2ede4,#f2ede4 3px,#fff 3px,#fff 6px)" }} />} label="Away" />
        <Legend swatch={<Box sx={{ width: 12, height: 12, borderRadius: "3px", border: `1px solid ${accent}` }} />} label="Today" />
      </Box>
    </Box>
  );
}

function Legend({ swatch, label }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      {swatch}
      <Typography sx={{ fontFamily: font, fontSize: 11, color: textMuted }}>{label}</Typography>
    </Box>
  );
}
